<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { BucketCreateDocument } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BModal,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue-next';
import { BUCKET_DATA_TYPES } from '@/constants';
import CloudRegionSelection from '@/components/compute/CloudRegionSelection.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useUserStore } from '@/store/user';

const userStore = useUserStore();

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  defaultDataType: string;
}>();

// --- Component emits ---
const emits = defineEmits(['success', 'close']);

// --- Component state ---
const show = computed<boolean>(() => {
  return props.show;
});
const bucketName = ref<string>('');
const bucketFriendlyName = ref<string>('');
const description = ref<string>('');
const dataType = computed<string>(() => {
  return props.defaultDataType || BUCKET_DATA_TYPES.INPUT;
});
const expirationDays = ref<number>(30);
const labels = ref<string>(''); // Comma-separated labels input
const customCloudRegionPrefix = ref<string | null>(null);
const customCloudRegionRequired = ref(false);

// --- Mutation: Rename Data Object ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(BucketCreateDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.bucketCreate) {
    console.debug('Bucket created:', res.data.bucketCreate);
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('success');
    }, 1000);
  } else {
    mutationError.value = 'Dataset creation failed: ' + res.data;
    console.error('Bucket creation failed:', res.data);
  }
});
onMutationError((err) => {
  console.error('Error deleting bucket:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handle component actions ---
function onMutationConfirmed() {
  mutationInProgress.value = true;
  mutationError.value = null;

  executeMutation({
    friendlyName: bucketFriendlyName.value,
    name: bucketName.value ? bucketName.value : undefined,
    description: description.value,
    dataType: dataType.value,
    region: customCloudRegionPrefix.value
      ? customCloudRegionPrefix.value
      : undefined,
    expiresInDays: expirationDays.value > 0 ? expirationDays.value : undefined,
    labels: labels.value
      ? labels.value
          .split(';')
          .map((label) => label.trim())
          .filter((label) => label.length > 0)
      : undefined,
  });
}

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <div>
    <!-- MODAL -->
    <b-modal v-model="show" @hide="handleCloseModal">
      <!-- Title -->
      <template #title>
        <font-awesome-icon icon="database" class="me-2" />
        Create New Dataset
      </template>

      <!-- Content -->
      <div>
        <p>
          This is a developer-only feature for creating new empty datasets. If
          you are developer or administrator, use this to create a new empty
          storage bucket. Then you can extract the bucket ID and upload data
          directly from your dev machine, GCE, AWS or TrueNAS using CLI tools
          such as <i>gsutil</i>.
        </p>
        <!-- Bucket Name -->
        <BFormGroup v-if="userStore.isAdmin">
          <label for="bucket-name" class="fw-bold">
            <font-awesome-icon icon="tag" class="me-1" />
            Bucket name
            <font-awesome-icon
              icon="shield-halved"
              class="ms-1 text-muted"
              id="icon-admin-bucket-name"
            />
          </label>
          <b-form-input
            v-model="bucketName"
            required
            id="bucket-name"
            type="text"
            placeholder="Leave empty for auto-generated name"
            :disabled="mutationInProgress || mutationShowSuccess"
          />
          <BTooltip target="icon-admin-bucket-name" triggers="hover focus">
            Optional unique bucket name. If left empty, a unique name will be
            generated. The bucket name can only contain lowercase letters,
            numbers, and hyphens, and must start with a letter.
          </BTooltip>
        </BFormGroup>

        <!-- Bucket Friendly Name -->
        <BFormGroup class="mt-3">
          <label for="bucket-friendly-name" class="fw-bold">
            <font-awesome-icon icon="tag" class="me-1" />
            Dataset Name
          </label>
          <b-form-input
            v-model="bucketFriendlyName"
            required
            id="bucket-friendly-name"
            type="text"
            placeholder="Type in dataset name"
            :disabled="mutationInProgress || mutationShowSuccess"
          />
        </BFormGroup>

        <!-- Description -->
        <BFormGroup class="mt-3">
          <label for="bucket-description" class="fw-bold">
            <font-awesome-icon icon="align-left" class="me-1" />
            Description
          </label>
          <b-form-textarea
            id="bucket-description"
            v-model="description"
            placeholder="Type in dataset description"
            :disabled="mutationInProgress || mutationShowSuccess"
          />
        </BFormGroup>

        <!-- Bucket Type - Select (input, output) -->
        <BFormGroup class="mt-3">
          <label for="bucket-type" class="fw-bold">
            <font-awesome-icon icon="database" class="me-1" />
            Dataset Type
          </label>
          <b-form-select
            id="bucket-type"
            v-model="dataType"
            :options="[
              { value: BUCKET_DATA_TYPES.INPUT, text: 'input' },
              { value: BUCKET_DATA_TYPES.OUTPUT, text: 'output' },
              ...(userStore.isAdmin
                ? [{ value: BUCKET_DATA_TYPES.SYSTEM, text: 'system' }]
                : []),
            ]"
            :disabled="mutationInProgress || mutationShowSuccess"
          />
        </BFormGroup>

        <!-- Expiration (default 30 days) -->
        <BFormGroup class="mt-3" v-if="userStore.isAdmin">
          <label for="bucket-expiration" class="fw-bold">
            <font-awesome-icon icon="clock" class="me-1" />
            Expiration in days
            <font-awesome-icon
              icon="shield-halved"
              class="ms-1 text-muted"
              id="icon-admin-expiration"
            />
            <font-awesome-icon
              icon="circle-question"
              class="ms-1 text-muted"
              id="icon-help-expiration"
            />
          </label>
          <b-form-input
            id="bucket-expiration"
            type="number"
            :min="userStore.isAdmin ? 0 : 1"
            :max="userStore.isAdmin ? 365 : 30"
            v-model="expirationDays"
            :disabled="mutationInProgress || mutationShowSuccess"
          />
          <BTooltip target="icon-admin-expiration" triggers="hover focus">
            Admins can set expiration to 0 for no expiration or up to 365 days.
            For non-admins, the value is set to 30 days automatically.
          </BTooltip>
          <BTooltip target="icon-help-expiration" triggers="hover focus">
            Number of days until the dataset expires. After expiration, the
            dataset will be automatically deleted.
            <span v-if="userStore.isAdmin">Set to 0 for no expiration.</span>
          </BTooltip>
        </BFormGroup>

        <!-- Labels (strings separated by semicolons) -->
        <BFormGroup class="mt-3" v-if="userStore.isAdmin">
          <label for="bucket-labels" class="fw-bold">
            <font-awesome-icon icon="layer-group" class="me-1" />
            Labels
            <font-awesome-icon
              icon="shield-halved"
              class="ms-1 text-muted"
              id="icon-admin-labels"
            />
            <font-awesome-icon
              icon="circle-question"
              class="text-muted ms-1"
              id="icon-help-labels"
            />
          </label>
          <b-form-input
            id="bucket-labels"
            type="text"
            placeholder="Semicolon-separated labels"
            v-model="labels"
            :disabled="mutationInProgress || mutationShowSuccess"
          />
          <BTooltip target="icon-help-labels" triggers="hover focus">
            Optional labels to help organize and filter datasets. Enter as
            semicolon-separated strings in `key` or `key:value` format (e.g.
            'watchdog-exempt;integration-tests:true;length:255').
          </BTooltip>
        </BFormGroup>

        <!-- Cloud Region -->
        <CloudRegionSelection
          v-model="customCloudRegionPrefix"
          v-model:modelRequired="customCloudRegionRequired"
          class="mt-3"
        />

        <!-- Error Message -->
        <b-alert
          v-model="mutationError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="mutationError = null"
        >
          {{ mutationError }}
        </b-alert>
      </div>

      <!-- Footer -->
      <template #footer>
        <b-button variant="secondary" @click="handleCloseModal">
          Cancel
        </b-button>
        <!-- submit button -->
        <b-button
          variant="outline-primary"
          :disabled="
            mutationInProgress || mutationShowSuccess || !bucketFriendlyName
          "
          @click="onMutationConfirmed"
        >
          <font-awesome-icon icon="plus" class="me-1" />
          <span v-if="mutationInProgress">
            Creating Dataset
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="mutationShowSuccess">
            Dataset Created
            <font-awesome-icon :icon="['fa', 'check-double']" />
          </span>
          <span v-else> Create Dataset </span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
<style scoped>
.confirmation-text {
  color: white;
  background-color: #6c757d;
  border-radius: 0.25rem;
  padding: 0.15rem 0.3rem;
  font-size: 0.75rem;
  font-weight: 600;
}
</style>
